import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Button,
  Progress,
  Space,
  Typography,
  Badge,
  Tag,
  Spin,
  Empty,
  Breadcrumb,
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  message,
  Popconfirm,
  Tooltip,
  Tour,
} from 'antd';
import {
  FileTextOutlined,
  EditOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  FormatPainterOutlined,
  SketchOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import {
  v2AnalysisApi,
  v2ArticlesApi,
  v2ToppingsApi,
  v2StylesApi,
  v2SystemUpdatesApi,
  gamificationApi,
} from '../../../api';
import { getUserProfile } from '../../../auth';
import TipsRotator from '../../Common/TipsRotator';
import { generalTips } from '../../Common/tipsData';
import dayjs from 'dayjs';
import './AIODashboard.css';
import GamificationSummary from '../../Gamification/GamificationSummary';
import RankUpModal from '../../Gamification/RankUpModal';
import { useTutorialTour } from '../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../config/tourConfigs';
import EnhancedTour, { TourHelpButton } from '../../Common/EnhancedTour';
import PushNotificationButton from '../../Common/PushNotificationButton';

const { Title, Text, Paragraph } = Typography;

// カウントアップアニメーション用のフック
const useCountUp = (end, duration = 1500, start = 0) => {
  const [count, setCount] = useState(start);

  useEffect(() => {
    if (end === start) {
      setCount(end);
      return;
    }

    let startTime;
    const step = timestamp => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // イージング関数（ease-out）
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(start + (end - start) * easeOutQuart));

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(step);
  }, [end, duration, start]);

  return count;
};

// ステータス表示マップ（生成結果一覧と同じ）
const statusMap = {
  queued: { text: '準備中', color: 'default' },
  step2: { text: 'ベース選択中', color: 'processing' },
  step3: { text: '構成編集中', color: 'geekblue' },
  step4_1: { text: '執筆計画中', color: 'cyan' },
  step4_2: { text: '本文生成中', color: 'blue' },
  step4_3: { text: '推敲中', color: 'purple' },
  step4_4: { text: '最終調整中', color: 'orange' },
  step4: { text: '執筆計画中', color: 'cyan' },
  step4_ready: { text: '執筆計画中', color: 'cyan' },
  writing: { text: '本文生成中', color: 'blue' },
  reviewing: { text: '推敲中', color: 'purple' },
  finalizing: { text: '最終調整中', color: 'orange' },
  draft: { text: 'ベース選択中', color: 'processing' },
  generating: { text: '本文生成中', color: 'blue' },
  completed: { text: '生成完了', color: 'success' },
  error: { text: 'エラー', color: 'error' },
  cancelled: { text: 'キャンセル', color: 'default' },
};

// ステータス正規化（生成結果一覧と同じロジック）
const normalizeStatus = rec => {
  const s = String(rec.status || '').toLowerCase();
  const step = String(rec.current_step || '').toLowerCase();

  // ⭐ status='completed' を最優先で判定（生成完了）
  if (s === 'completed') return 'completed';

  // step4_completed は current_step に基づいて判定
  if (s === 'step4_completed') {
    if (step === '4.4' || step === 'step4.4') return 'step4_4'; // 最終調整中
    if (step === '4.3' || step === 'step4.3') return 'step4_3'; // 推敲中
    // 本文生成完了後は推敲フェーズへ移行
    return 'step4_3';
  }

  // current_step を詳細に判定
  if (step === '4.1' || step === 'step4.1') return 'step4_1';
  if (step === '4.2' || step === 'step4.2') return 'step4_2';
  if (step === '4.3' || step === 'step4.3') return 'step4_3';
  if (step === '4.4' || step === 'step4.4') return 'step4_4';
  if (step.startsWith('4')) return 'step4';
  if (step === 'step3' || step === '3') return 'step3';
  if (step === 'step2' || step === '2') return 'step2';

  // status で判断
  if (s === 'step4_ready') return 'step4_ready';
  if (s === 'writing') return 'writing';
  if (s === 'reviewing') return 'reviewing';
  if (s === 'finalizing') return 'finalizing';
  if (s === 'error') return 'error';
  if (s === 'cancelled') return 'cancelled';

  // それ以外は準備中扱い
  return 'queued';
};

const AIODashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [statistics, setStatistics] = useState({
    totalGenerations: 0,
    completedGenerations: 0,
    totalCharacters: 0,
    totalAnalyses: 0,
    totalStyles: 0,
    totalToppings: 0,
    savedHours: 0,
    totalLogins: 0, // ログイン日数のみゲーミフィケーションから
  });
  const [featureStatus, setFeatureStatus] = useState({
    style_fine_tuning: false,
    topping_data: false,
  });
  const [activeAnalyses, setActiveAnalyses] = useState([]);
  const [activeGenerations, setActiveGenerations] = useState([]);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [recentGenerations, setRecentGenerations] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [form] = Form.useForm();
  const [rankUpData, setRankUpData] = useState(null);
  const [rankUpModalVisible, setRankUpModalVisible] = useState(false);

  // チュートリアルTour用のref
  const rankProgressRef = useRef(null);
  const statisticsRef = useRef(null);
  const recentResearchRef = useRef(null);
  const recentGenerationRef = useRef(null);
  const sidebarRef = useRef(null);

  // チュートリアルTour管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    tourCompleted,
    tourSkipped,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('dashboard');

  const handleRankUp = rankUpInfo => {
    setRankUpData(rankUpInfo);
    setRankUpModalVisible(true);
  };

  useEffect(() => {
    initializeDashboard();
    const interval = setInterval(() => {
      if (!isGuest) loadActiveTasksOnly();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const initializeDashboard = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setIsGuest(true);
      setLoading(false);
      return;
    }
    try {
      const profile = await getUserProfile();
      setUserProfile(profile);
      await loadDashboardData(profile);
    } catch (error) {
      console.error('Failed to initialize dashboard:', error);
      setIsGuest(true);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async (profile = userProfile) => {
    try {
      await Promise.all([
        loadStatistics(),
        loadActiveAnalyses(),
        loadActiveGenerations(),
        loadRecentAnalyses(),
        loadRecentGenerations(),
        loadUpdates(profile),
      ]);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const loadActiveTasksOnly = async () => {
    try {
      await Promise.all([loadActiveAnalyses(), loadActiveGenerations()]);
    } catch (error) {
      console.error('Failed to load active tasks:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      // ゲーミフィケーションステータスから取得（ログイン回数用）
      const gamificationStatus = await gamificationApi.getStatus();

      // 機能アンロック状態を取得
      const featuresResponse = await gamificationApi.getAllFeaturesStatus();
      const features = featuresResponse.features || featuresResponse;
      setFeatureStatus({
        style_fine_tuning: features.style_fine_tuning?.unlocked || false,
        topping_data: features.topping_data?.unlocked || false,
      });

      // 生成記事の統計（最適化版：全件取得せずに統計APIを使用）
      const articleStats = await v2ArticlesApi.getArticleStats();
      const totalGenerationsCount = articleStats?.total_count || 0;
      const completedGenerationsCount = articleStats?.completed_count || 0;
      const totalCharacters = articleStats?.total_characters || 0;

      // リサーチ数（分析完了済み）- 管理者でも自分のもののみ
      const analysesResponse = await v2AnalysisApi.listAnalyses({
        skip: 0,
        limit: 5, // 件数だけ知りたいので少なめ
        my_only: true,
      });
      const totalAnalysesCount = analysesResponse?.total_count || 0;

      // スタイル数（デフォルトスタイルを除く）
      const stylesResponse = await v2StylesApi.getStyles({ limit: 1000 });
      const styles = stylesResponse?.data || stylesResponse || [];
      const customStyles = styles.filter(s => !s.is_default);

      // 独自情報数
      const toppingsResponse = await v2ToppingsApi.getToppings({ limit: 500 });
      const toppings =
        toppingsResponse?.data?.data ||
        toppingsResponse?.data ||
        toppingsResponse ||
        [];

      // 節約時間の計算（1リサーチ=3時間、1生成=5時間）
      const savedHours =
        totalAnalysesCount * 3 + completedGenerationsCount * 5;

      const stats = {
        totalGenerations: totalGenerationsCount,
        completedGenerations: completedGenerationsCount,
        totalCharacters: totalCharacters,
        totalAnalyses: totalAnalysesCount,
        totalStyles: customStyles.length,
        totalToppings: Array.isArray(toppings) ? toppings.length : 0,
        savedHours: savedHours,
        totalLogins: gamificationStatus?.daily_login_count || 0,
      };
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const loadActiveAnalyses = async () => {
    try {
      const response = await v2AnalysisApi.getActiveAnalyses();
      setActiveAnalyses(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load active analyses:', error);
      setActiveAnalyses([]);
    }
  };

  const loadActiveGenerations = async () => {
    try {
      const response = await v2ArticlesApi.getArticles({
        skip: 0,
        limit: 20,
        status: 'running',
      });
      const articles = response?.articles || response || [];
      const activeGens = articles.filter(
        g => g.status === 'running' || g.status === 'queued',
      );
      setActiveGenerations(activeGens);
    } catch (error) {
      console.error('Failed to load active generations:', error);
      setActiveGenerations([]);
    }
  };

  const loadRecentAnalyses = async () => {
    try {
      // 管理者も自分のリサーチのみ表示するためmy_only=trueを指定
      const response = await v2AnalysisApi.listAnalyses({
        skip: 0,
        limit: 5,
        my_only: true,
      });
      console.log('🔍 Recent analyses response:', response);
      const analyses = response?.analyses || [];
      setRecentAnalyses(analyses);
    } catch (error) {
      console.error('Failed to load recent analyses:', error);
      setRecentAnalyses([]);
    }
  };

  const loadRecentGenerations = async () => {
    try {
      // 管理者も自分の生成のみ表示するためmy_only=trueを指定
      const response = await v2ArticlesApi.getArticles({
        skip: 0,
        limit: 5,
        my_only: true,
      });
      const articles = response?.articles || response || [];
      console.log('📝 Recent generations response:', response);
      console.log(
        '📝 Recent generations statuses:',
        articles.map(g => ({
          title: g.title || g.keyword,
          status: g.status,
          progress: g.progress,
        })),
      );
      setRecentGenerations(articles);
    } catch (error) {
      console.error('Failed to load recent generations:', error);
      setRecentGenerations([]);
    }
  };

  const loadUpdates = async (profile = userProfile) => {
    try {
      const isMaster = profile?.role === 'master';
      console.log(
        '🔔 Loading updates, isMaster:',
        isMaster,
        'profile:',
        profile,
      );
      const response = await v2SystemUpdatesApi.getUpdates({
        limit: 10,
        include_unpublished: isMaster,
      });
      console.log('🔔 Updates response:', response);
      setUpdates(response || []);
    } catch (error) {
      console.error('Failed to load updates:', error);
      setUpdates([]);
    }
  };

  const handleAddUpdate = () => {
    setEditingUpdate(null);
    form.resetFields();
    form.setFieldsValue({
      is_published: true,
      display_order: 0,
    });
    setUpdateModalVisible(true);
  };

  const handleEditUpdate = update => {
    setEditingUpdate(update);
    form.setFieldsValue({
      ...update,
      release_date: dayjs(update.release_date),
    });
    setUpdateModalVisible(true);
  };

  const handleDeleteUpdate = async updateId => {
    try {
      await v2SystemUpdatesApi.deleteUpdate(updateId);
      message.success('アップデート情報を削除しました');
      await loadUpdates(userProfile);
    } catch (error) {
      message.error('削除に失敗しました');
      console.error('Failed to delete update:', error);
    }
  };

  const handleSaveUpdate = async () => {
    try {
      const values = await form.validateFields();
      const updateData = {
        ...values,
        release_date: values.release_date.format('YYYY-MM-DD'),
      };

      if (editingUpdate) {
        await v2SystemUpdatesApi.updateUpdate(editingUpdate.id, updateData);
        message.success('アップデート情報を更新しました');
      } else {
        await v2SystemUpdatesApi.createUpdate(updateData);
        message.success('アップデート情報を作成しました');
      }

      setUpdateModalVisible(false);
      await loadUpdates(userProfile);
    } catch (error) {
      if (error.errorFields) {
        message.error('入力内容を確認してください');
      } else {
        message.error('保存に失敗しました');
        console.error('Failed to save update:', error);
      }
    }
  };

  const getStatusTag = generation => {
    const norm = normalizeStatus(generation);
    const meta = statusMap[norm] || {
      text: generation.status,
      color: 'default',
    };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const getStatusConfig = status => {
    const configs = {
      completed: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: '完了',
      },
      step4_completed: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: '完了',
      },
      running: {
        color: 'processing',
        icon: <SyncOutlined spin />,
        text: '実行中',
      },
      generating: {
        color: 'processing',
        icon: <SyncOutlined spin />,
        text: '生成中',
      },
      step4_ready: {
        color: 'processing',
        icon: <SyncOutlined spin />,
        text: '生成中',
      },
      queued: {
        color: 'default',
        icon: <ClockCircleOutlined />,
        text: '待機中',
      },
      error: { color: 'error', icon: <CloseCircleOutlined />, text: 'エラー' },
    };
    return (
      configs[status] || {
        color: 'default',
        icon: <ClockCircleOutlined />,
        text: status || '不明',
      }
    );
  };

  const formatNumber = num => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatCharacters = num => {
    // 万字単位で表示
    if (num >= 10000) {
      return (num / 10000).toFixed(1);
    }
    return (num / 10000).toFixed(2);
  };

  const formatSavedTime = hours => {
    // 日単位で表示
    const days = hours / 24;
    if (days >= 10) {
      return days.toFixed(1);
    }
    return days.toFixed(2);
  };

  // カウントアップアニメーション用
  // カウントアップアニメーション
  const animatedTotalGenerations = useCountUp(
    statistics.totalGenerations,
    1500,
  );
  const animatedCompletedGenerations = useCountUp(
    statistics.completedGenerations,
    1500,
  );
  const animatedCharacters = useCountUp(statistics.totalCharacters, 1500);
  const animatedAnalyses = useCountUp(statistics.totalAnalyses, 1500);
  const animatedStyles = useCountUp(statistics.totalStyles, 1500);
  const animatedToppings = useCountUp(statistics.totalToppings, 1500);
  const animatedSavedHours = useCountUp(statistics.savedHours, 1500);
  const animatedLogins = useCountUp(statistics.totalLogins, 1500);

  const isMaster = userProfile?.role === 'master';

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Tourステップ定義
  const tourSteps = [
    {
      title: TOUR_CONFIGS.dashboard.steps[0].title,
      description: TOUR_CONFIGS.dashboard.steps[0].description,
      target: null,
    },
    {
      title: TOUR_CONFIGS.dashboard.steps[1].title,
      description: TOUR_CONFIGS.dashboard.steps[1].description,
      target: () => rankProgressRef.current,
      placement: 'right',
    },
    {
      title: TOUR_CONFIGS.dashboard.steps[2].title,
      description: TOUR_CONFIGS.dashboard.steps[2].description,
      target: () => statisticsRef.current,
      placement: 'left',
    },
    {
      title: TOUR_CONFIGS.dashboard.steps[3].title,
      description: TOUR_CONFIGS.dashboard.steps[3].description,
      target: () => recentResearchRef.current,
      placement: 'top',
    },
    {
      title: TOUR_CONFIGS.dashboard.steps[4].title,
      description: TOUR_CONFIGS.dashboard.steps[4].description,
      target: () => recentGenerationRef.current,
      placement: 'top',
    },
    {
      title: TOUR_CONFIGS.dashboard.steps[5].title,
      description: TOUR_CONFIGS.dashboard.steps[5].description,
      target: () => document.querySelector('.ant-layout-sider'),
      placement: 'right',
    },
  ];

  return (
    <div className="aio-dashboard">
      {/* チュートリアルTour（拡張版） */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          current={currentStep}
          onChange={updateCurrentStep}
          onClose={async () => {
            await completeTour();
            // ツアー完了後、ランクアップモーダルを表示するためにリロード
            window.location.reload();
          }}
          onFinish={async () => {
            await completeTour();
            // ツアー完了後、ランクアップモーダルを表示するためにリロード
            window.location.reload();
          }}
          onSkip={skipTour}
          allowSkip={true}
          tourTitle="ダッシュボードガイド"
          mask={true}
        />
      )}
      {/* パンくずリスト */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Breadcrumb
          items={[
            {
              title: <Link to="/aio/dashboard">AIOコンテンツモード</Link>,
            },
          ]}
        />
        <TourHelpButton
          onClick={restartTour}
          tooltip="ガイドツアーを再表示"
        />
      </div>

      {/* ページヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title
              level={1}
              className="gradient-text"
              style={{ fontSize: 24, marginBottom: 0 }}
            >
              ダッシュボード
            </Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              リサーチから生成まで、コンテンツ制作の全フローを管理
            </Typography.Text>
          </Col>
          <Col>
            <PushNotificationButton type="default" size="middle" showText={true} className="push-notification-btn" />
          </Col>
        </Row>
      </div>

      {/* ヒント */}
      <TipsRotator tips={generalTips} intervalMs={7000} fadeMs={300} />

      {/* ランク進捗＋統計サマリー */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {/* 左カラム：ランク進捗 */}
        <Col xs={24} xl={8}>
          <div ref={rankProgressRef}>
            {!isGuest ? (
              <GamificationSummary onRankUp={handleRankUp} />
            ) : (
              <Card style={{ height: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>magicssを始めよう</Title>
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    無料登録でリサーチ・記事生成を体験できます
                  </Paragraph>
                  <Button type="primary" onClick={() => navigate('/register')}>
                    無料で始める
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </Col>

        {/* 右カラム：統計情報（3×2段） */}
        <Col xs={24} xl={16}>
          <Row gutter={[16, 16]} ref={statisticsRef}>
            <Col xs={24} sm={12} xl={8}>
              <Card className="stat-card">
                <div style={{ textAlign: 'center' }}>
                  <Tooltip title="累計ログイン日数です。毎日ログインしてクレジットを獲得しましょう。">
                    <Statistic
                      title="ログイン回数"
                      value={animatedLogins}
                      prefix={
                        <CheckCircleOutlined style={{ fontSize: '20px' }} />
                      }
                      suffix={<span className="stat-suffix">日</span>}
                    />
                  </Tooltip>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Card className="stat-card">
                <div style={{ textAlign: 'center' }}>
                  <Tooltip title="キーワードリサーチを実行した累計回数です。競合分析やSERP解析を含みます。">
                    <Statistic
                      title="累計リサーチ数"
                      value={animatedAnalyses}
                      prefix={
                        <FileSearchOutlined style={{ fontSize: '20px' }} />
                      }
                      suffix={<span className="stat-suffix">件</span>}
                    />
                  </Tooltip>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Card className="stat-card">
                <div style={{ textAlign: 'center' }}>
                  <Tooltip title="記事の本文生成が完了した数と総実行数です。">
                    <Statistic
                      title="累計生成数"
                      value={`${animatedCompletedGenerations} / ${animatedTotalGenerations}`}
                      prefix={<FileTextOutlined style={{ fontSize: '20px' }} />}
                      suffix={<span className="stat-suffix">件</span>}
                    />
                  </Tooltip>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Card className="stat-card">
                <div style={{ textAlign: 'center' }}>
                  <Tooltip title="生成完了した記事の累計文字数です。高品質な長文コンテンツを効率的に作成できます。">
                    <Statistic
                      title="累計文字数"
                      value={formatCharacters(animatedCharacters)}
                      prefix={<EditOutlined style={{ fontSize: '20px' }} />}
                      suffix={<span className="stat-suffix">万字</span>}
                    />
                  </Tooltip>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Card className="stat-card">
                <div style={{ textAlign: 'center' }}>
                  <Tooltip title="リサーチと生成で節約できた時間です。手動作業と比較して大幅な時間短縮を実現します。">
                    <Statistic
                      title="節約時間"
                      value={formatSavedTime(animatedSavedHours)}
                      prefix={
                        <ClockCircleOutlined style={{ fontSize: '20px' }} />
                      }
                      suffix={<span className="stat-suffix">日</span>}
                    />
                  </Tooltip>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={4}>
              <Card className="stat-card" bodyStyle={{ padding: '24px 16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <Tooltip
                    title={
                      featureStatus.style_fine_tuning
                        ? '登録済みのスタイル数です。文体やトーンを統一して、ブランドに合った記事を生成できます。'
                        : 'スタイル登録機能はライトプラン以上で利用可能です。'
                    }
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          color: '#8c8c8c',
                          fontWeight: 500,
                          marginBottom: 4,
                        }}
                      >
                        スタイル
                      </div>
                      {featureStatus.style_fine_tuning ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                          }}
                        >
                          <FormatPainterOutlined
                            style={{
                              fontSize: '18px',
                              color: '#1890ff',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 24,
                              fontWeight: 600,
                              color: 'rgba(0, 0, 0, 0.85)',
                            }}
                          >
                            {animatedStyles}
                          </span>
                          <span
                            style={{
                              fontSize: 14,
                              color: 'rgba(0, 0, 0, 0.45)',
                              marginLeft: -4,
                            }}
                          >
                            個
                          </span>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '44px',
                          }}
                        >
                          <LockOutlined
                            style={{
                              fontSize: '28px',
                              color: '#d9d9d9',
                              margin: 0,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={4}>
              <Card className="stat-card" bodyStyle={{ padding: '24px 16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <Tooltip
                    title={
                      featureStatus.topping_data
                        ? '独自情報（トッピング）の登録数です。自社の強みや専門知識を記事に反映できます。'
                        : '独自情報機能はスタンダードプラン以上で利用可能です。'
                    }
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          color: '#8c8c8c',
                          fontWeight: 500,
                          marginBottom: 4,
                        }}
                      >
                        独自情報
                      </div>
                      {featureStatus.topping_data ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                          }}
                        >
                          <SketchOutlined
                            style={{
                              fontSize: '18px',
                              color: '#1890ff',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 24,
                              fontWeight: 600,
                              color: 'rgba(0, 0, 0, 0.85)',
                            }}
                          >
                            {animatedToppings}
                          </span>
                          <span
                            style={{
                              fontSize: 14,
                              color: 'rgba(0, 0, 0, 0.45)',
                              marginLeft: -4,
                            }}
                          >
                            個
                          </span>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '44px',
                          }}
                        >
                          <LockOutlined
                            style={{
                              fontSize: '28px',
                              color: '#d9d9d9',
                              margin: 0,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* 進行中のタスク */}
      {(activeAnalyses.length > 0 || activeGenerations.length > 0) && (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <SyncOutlined spin />
                  <span>進行中のタスク</span>
                  <Badge
                    count={activeAnalyses.length + activeGenerations.length}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                </Space>
              }
              className="active-tasks-card"
            >
              {activeAnalyses.length > 0 && (
                <>
                  <Title level={5} style={{ marginTop: 0 }}>
                    実行中のリサーチ ({activeAnalyses.length}件)
                  </Title>
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size={12}
                  >
                    {activeAnalyses.map(analysis => (
                      <div key={analysis.analysis_id} className="task-item">
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                          }}
                        >
                          <Text strong>{analysis.keyword}</Text>
                          <Tag color={getStatusConfig(analysis.status).color}>
                            {getStatusConfig(analysis.status).icon}{' '}
                            {getStatusConfig(analysis.status).text}
                          </Tag>
                        </div>
                        <Progress
                          percent={analysis.progress || 0}
                          status="active"
                          strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 8,
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {analysis.current_step || 'リサーチ実行中...'}
                          </Text>
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              navigate(`/aio/analysis/${analysis.analysis_id}`)
                            }
                          >
                            詳細
                          </Button>
                        </div>
                      </div>
                    ))}
                  </Space>
                </>
              )}

              {activeGenerations.length > 0 && activeAnalyses.length > 0 && (
                <div
                  style={{ margin: '16px 0', borderTop: '1px solid #f0f0f0' }}
                />
              )}

              {activeGenerations.length > 0 && (
                <>
                  <Title
                    level={5}
                    style={{ marginTop: activeAnalyses.length > 0 ? 16 : 0 }}
                  >
                    実行中の生成 ({activeGenerations.length}件)
                  </Title>
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size={12}
                  >
                    {activeGenerations.map(generation => (
                      <div key={generation.uuid} className="task-item">
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                          }}
                        >
                          <Text strong>
                            {generation.title || generation.keyword}
                          </Text>
                          <Tag color={getStatusConfig(generation.status).color}>
                            {getStatusConfig(generation.status).icon}{' '}
                            {getStatusConfig(generation.status).text}
                          </Tag>
                        </div>
                        <Progress
                          percent={generation.progress || 0}
                          status="active"
                          strokeColor={{
                            '0%': '#722ed1',
                            '100%': '#87d068',
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 8,
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {generation.current_step || '生成実行中...'}
                          </Text>
                          <Button
                            type="link"
                            size="small"
                            onClick={() => {
                              const currentStep = String(
                                generation.current_step || '',
                              ).toLowerCase();
                              if (
                                currentStep === '4.2' ||
                                currentStep === 'step4.2'
                              ) {
                                navigate(
                                  `/aio/generation/${generation.uuid}/step4-2`,
                                );
                              } else if (
                                currentStep === '4.3' ||
                                currentStep === 'step4.3'
                              ) {
                                navigate(
                                  `/aio/generation/${generation.uuid}/step4-3`,
                                );
                              } else if (
                                currentStep === '4.4' ||
                                currentStep === 'step4.4'
                              ) {
                                navigate(
                                  `/aio/generation/${generation.uuid}/step4-4`,
                                );
                              } else {
                                navigate(
                                  `/aio/generation/${generation.uuid}/step4-1`,
                                );
                              }
                            }}
                          >
                            詳細
                          </Button>
                        </div>
                      </div>
                    ))}
                  </Space>
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* 最近の活動 */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            ref={recentResearchRef}
            title="直近のリサーチ"
            className="recent-card"
            extra={
              <div className="recent-card-actions">
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/aio/analysis/new')}
                >
                  新規実行
                </Button>
                <Button
                  type="link"
                  onClick={() => navigate('/aio/analysis')}
                  icon={<ArrowRightOutlined />}
                >
                  すべて見る
                </Button>
              </div>
            }
          >
            {recentAnalyses.length === 0 ? (
              <Empty
                description="リサーチ履歴がありません"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {recentAnalyses.map(analysis => (
                  <div
                    key={analysis.analysis_id}
                    className="recent-item"
                    onClick={() =>
                      navigate(`/aio/analysis/${analysis.analysis_id}`)
                    }
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Space>
                        <Text strong>{analysis.keyword}</Text>
                        <Tag
                          color={getStatusConfig(analysis.status).color}
                          icon={getStatusConfig(analysis.status).icon}
                        >
                          {getStatusConfig(analysis.status).text}
                        </Tag>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {analysis.created_at
                          ? dayjs(analysis.created_at).format('MM/DD HH:mm')
                          : '-'}
                      </Text>
                    </div>
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            ref={recentGenerationRef}
            title="直近の生成"
            className="recent-card"
            extra={
              <div className="recent-card-actions">
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/aio/generation/new')}
                >
                  新規実行
                </Button>
                <Button
                  type="link"
                  onClick={() => navigate('/aio/generation')}
                  icon={<ArrowRightOutlined />}
                >
                  すべて見る
                </Button>
              </div>
            }
          >
            {recentGenerations.length === 0 ? (
              <Empty
                description="生成履歴がありません"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {recentGenerations.map(generation => (
                  <div
                    key={generation.uuid}
                    className="recent-item"
                    onClick={() => {
                      const norm = normalizeStatus(generation);
                      const currentStep = String(
                        generation.current_step || '',
                      ).toLowerCase();

                      // 生成完了済み → STEP4.4へ
                      if (norm === 'completed') {
                        navigate(`/aio/generation/${generation.uuid}/step4-4`);
                        return;
                      }

                      // STEP4のサブステップに応じた遷移
                      if (currentStep === '4.4' || currentStep === 'step4.4') {
                        navigate(`/aio/generation/${generation.uuid}/step4-4`);
                      } else if (
                        currentStep === '4.3' ||
                        currentStep === 'step4.3'
                      ) {
                        navigate(`/aio/generation/${generation.uuid}/step4-3`);
                      } else if (
                        currentStep === '4.2' ||
                        currentStep === 'step4.2'
                      ) {
                        navigate(`/aio/generation/${generation.uuid}/step4-2`);
                      } else if (
                        currentStep === '4.1' ||
                        currentStep === 'step4.1'
                      ) {
                        navigate(`/aio/generation/${generation.uuid}/step4-1`);
                      } else if (norm === 'step3') {
                        navigate(`/aio/generation/${generation.uuid}/step3`);
                      } else if (norm === 'step2') {
                        navigate(`/aio/generation/${generation.uuid}/step2`);
                      } else {
                        navigate(`/aio/generation/${generation.uuid}`);
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Space>
                        <Tooltip title={generation.title || ''}>
                          <Text strong>
                            {generation.keyword || generation.title || '-'}
                          </Text>
                        </Tooltip>
                        {getStatusTag(generation)}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {generation.created_at
                          ? dayjs(generation.created_at).format('MM/DD HH:mm')
                          : '-'}
                      </Text>
                    </div>
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      {/* アップデート情報 */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            title="アップデート情報"
            className="updates-card"
            extra={
              isMaster && (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddUpdate}
                >
                  追加
                </Button>
              )
            }
          >
            {updates.length === 0 ? (
              <Empty
                description="アップデート情報がありません"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                {updates.map((update, index) => (
                  <div key={update.id}>
                    <div className="update-item">
                      <Space
                        direction="vertical"
                        size={4}
                        style={{ width: '100%' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Space>
                            <Tag color="blue">{update.version}</Tag>
                            <Text strong>{update.title}</Text>
                            {!update.is_published && isMaster && (
                              <Tag color="orange">未公開</Tag>
                            )}
                          </Space>
                          <Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {update.release_date}
                            </Text>
                            {isMaster && (
                              <>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditUpdate(update)}
                                />
                                <Popconfirm
                                  title="削除しますか？"
                                  onConfirm={() =>
                                    handleDeleteUpdate(update.id)
                                  }
                                  okText="はい"
                                  cancelText="いいえ"
                                >
                                  <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                  />
                                </Popconfirm>
                              </>
                            )}
                          </Space>
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {update.description}
                        </Text>
                      </Space>
                    </div>
                    {index < updates.length - 1 && (
                      <div
                        style={{
                          borderTop: '1px solid #f0f0f0',
                          margin: '12px 0',
                        }}
                      />
                    )}
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      {/* アップデート情報編集モーダル */}
      <Modal
        title={
          editingUpdate ? 'アップデート情報を編集' : 'アップデート情報を追加'
        }
        open={updateModalVisible}
        onOk={handleSaveUpdate}
        onCancel={() => setUpdateModalVisible(false)}
        okText="保存"
        cancelText="キャンセル"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="version"
            label="バージョン"
            rules={[
              { required: true, message: 'バージョンを入力してください' },
            ]}
          >
            <Input placeholder="例: v2.6.0" />
          </Form.Item>
          <Form.Item
            name="title"
            label="タイトル"
            rules={[{ required: true, message: 'タイトルを入力してください' }]}
          >
            <Input placeholder="例: 新機能追加" />
          </Form.Item>
          <Form.Item
            name="description"
            label="説明"
            rules={[{ required: true, message: '説明を入力してください' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="例: ダッシュボード機能を追加しました"
            />
          </Form.Item>
          <Form.Item
            name="release_date"
            label="リリース日"
            rules={[
              { required: true, message: 'リリース日を選択してください' },
            ]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="is_published" label="公開" valuePropName="checked">
            <Switch checkedChildren="公開" unCheckedChildren="非公開" />
          </Form.Item>
          <Form.Item
            name="display_order"
            label="表示順序"
            tooltip="小さい数字ほど上に表示されます"
          >
            <Input type="number" placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ランクアップモーダル */}
      <RankUpModal
        visible={rankUpModalVisible}
        onClose={() => setRankUpModalVisible(false)}
        rankUpData={rankUpData}
      />
    </div>
  );
};

export default AIODashboard;
